import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, Minus, AlertTriangle, CheckCircle2, Info, XCircle, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ComposedChart, Line, Legend } from "recharts";
import api, { fmtIDR, fmtCompact } from "@/lib/api";
import { Money, StatusBadge } from "@/components/common";
import { downloadCsv } from "@/lib/exportCsv";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const card = "bg-[#0A0A0C] border border-[#1A1A22] rounded-md fade-up";
const title = "text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500";
const th = "px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-zinc-500";
const tooltipStyle = { contentStyle: { background: "#0A0A0C", border: "1px solid #26262E", borderRadius: 6, fontSize: 12 }, labelStyle: { color: "#a1a1aa" }, itemStyle: { color: "#e4e4e7" } };
const pad = (x) => String(x).padStart(2, "0");
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("id-ID", { month: "short", year: "2-digit" });
const num = (n, d = 0) => new Intl.NumberFormat("id-ID", { maximumFractionDigits: d }).format(Number(n) || 0);

export function presetRange(v) {
  const n = new Date();
  const mRange = (y, m) => [`${y}-${pad(m)}-01`, `${y}-${pad(m)}-${pad(new Date(y, m, 0).getDate())}`];
  if (v === "bulan-ini") return mRange(n.getFullYear(), n.getMonth() + 1);
  if (v === "bulan-lalu") { const d = new Date(n.getFullYear(), n.getMonth() - 1, 1); return mRange(d.getFullYear(), d.getMonth() + 1); }
  if (v === "3-bulan") { const d = new Date(n.getFullYear(), n.getMonth() - 2, 1); return [`${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`, n.toISOString().slice(0, 10)]; }
  if (v === "tahun-ini") return [`${n.getFullYear()}-01-01`, `${n.getFullYear()}-12-31`];
  if (v === "tahun-lalu") return [`${n.getFullYear() - 1}-01-01`, `${n.getFullYear() - 1}-12-31`];
  return ["2000-01-01", "2099-12-31"];
}

export function DateRangeBar({ start, end, setStart, setEnd, prefix, children }) {
  return (
    <div className="print-hide flex flex-wrap items-center gap-3 mb-5">
      <Select onValueChange={(v) => { const [s, e] = presetRange(v); setStart(s); setEnd(e); }}>
        <SelectTrigger data-testid={`${prefix}-preset-select`} className="w-40 h-10 bg-[#0F0F12] border-[#1A1A22]"><SelectValue placeholder="Filter Cepat" /></SelectTrigger>
        <SelectContent className="bg-[#0A0A0C] border-[#1A1A22]">
          <SelectItem value="bulan-ini">Bulan Ini</SelectItem>
          <SelectItem value="bulan-lalu">Bulan Lalu</SelectItem>
          <SelectItem value="3-bulan">3 Bulan Terakhir</SelectItem>
          <SelectItem value="tahun-ini">Tahun Ini</SelectItem>
          <SelectItem value="tahun-lalu">Tahun Lalu</SelectItem>
          <SelectItem value="semua">Semua Waktu</SelectItem>
        </SelectContent>
      </Select>
      <Input data-testid={`${prefix}-start-input`} type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-44 h-10 bg-[#0F0F12] border-[#1A1A22]" />
      <Input data-testid={`${prefix}-end-input`} type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-44 h-10 bg-[#0F0F12] border-[#1A1A22]" />
      {children}
    </div>
  );
}

export function ExportButtons({ prefix, onExcel, disabled }) {
  const cls = "h-10 px-4 inline-flex items-center gap-2 rounded-md border border-[#26262E] text-sm font-medium text-zinc-300 hover:bg-[#121218] transition-colors duration-200 disabled:opacity-40";
  return (
    <div className="ml-auto flex items-center gap-2">
      <button data-testid={`${prefix}-export-excel`} onClick={onExcel} disabled={disabled} className={cls}><Download className="w-4 h-4" /> Excel</button>
      <button data-testid={`${prefix}-export-pdf`} onClick={() => window.print()} disabled={disabled} className={cls}><Download className="w-4 h-4" /> PDF</button>
    </div>
  );
}

function Delta({ cur, prev, suffix = "", invert = false, pts = false }) {
  if (!prev && !cur) return <span className="text-xs text-zinc-600 inline-flex items-center gap-1"><Minus className="w-3 h-3" /> tidak ada data</span>;
  const diff = pts ? cur - prev : prev ? ((cur - prev) / Math.abs(prev)) * 100 : null;
  if (diff === null) return <span className="text-xs text-zinc-600">periode lalu: 0</span>;
  const up = diff >= 0;
  const good = invert ? !up : up;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`text-xs inline-flex items-center gap-1 font-medium ${Math.abs(diff) < 0.05 ? "text-zinc-500" : good ? "text-emerald-500" : "text-rose-500"}`}>
      <Icon className="w-3.5 h-3.5" /> {Math.abs(diff).toFixed(pts ? 1 : 0)}{pts ? " poin" : "%"}{suffix} vs periode lalu
    </span>
  );
}

function Kpi({ label, value, cur, prev, invert, pts, delay, testid }) {
  return (
    <div data-testid={testid} className={`${card} p-4`} style={{ animationDelay: `${delay}ms` }}>
      <p className={title}>{label}</p>
      <p className="font-heading font-extrabold text-xl text-zinc-50 mt-2 tabular-nums truncate">{value}</p>
      <div className="mt-1.5"><Delta cur={cur} prev={prev} invert={invert} pts={pts} /></div>
    </div>
  );
}

const LEVEL = {
  good: { icon: CheckCircle2, cls: "text-emerald-500 border-emerald-500/30 bg-emerald-500/5" },
  bad: { icon: XCircle, cls: "text-rose-500 border-rose-500/30 bg-rose-500/5" },
  warn: { icon: AlertTriangle, cls: "text-amber-500 border-amber-500/30 bg-amber-500/5" },
  info: { icon: Info, cls: "text-blue-500 border-blue-500/30 bg-blue-500/5" },
};

function Insights({ items }) {
  return (
    <div data-testid="insights-panel" className={`${card} p-4`}>
      <p className={`${title} mb-3`}>Insight & Rekomendasi</p>
      <div className="space-y-2">
        {items.map((it, i) => {
          const L = LEVEL[it.level] || LEVEL.info;
          return (
            <div key={i} data-testid={`insight-${it.level}`} className={`flex items-start gap-3 rounded-md border px-3 py-2.5 ${L.cls}`}>
              <L.icon className="w-4 h-4 mt-0.5 shrink-0" />
              <p className="text-sm text-zinc-200 leading-snug">{it.text}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Trend({ data }) {
  const d = data.map((x) => ({ ...x, label: monthLabel(x.month) }));
  return (
    <div data-testid="trend-chart" className={`${card} p-4`}>
      <p className={`${title} mb-4`}>Tren 12 Bulan — Omzet, Laba Bersih & Unit</p>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={d} barGap={4}>
            <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
            <XAxis dataKey="label" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis yAxisId="rp" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={fmtCompact} width={64} />
            <YAxis yAxisId="unit" orientation="right" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} width={30} />
            <Tooltip {...tooltipStyle} cursor={{ fill: "#27272a", opacity: 0.4 }} formatter={(v, n) => (n === "Unit" ? v : fmtIDR(v))} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar yAxisId="rp" dataKey="omzet" name="Omzet" fill="#0077C8" radius={[2, 2, 0, 0]} />
            <Bar yAxisId="rp" dataKey="laba" name="Laba Bersih" fill="#10b981" radius={[2, 2, 0, 0]} />
            <Line yAxisId="unit" type="monotone" dataKey="unit" name="Unit" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Aging({ aging, showHpp }) {
  const colors = ["text-emerald-500", "text-blue-500", "text-amber-500", "text-rose-500"];
  const slow = aging.items.filter((i) => i.days > 60);
  return (
    <div data-testid="aging-panel" className={`${card}`}>
      <div className="p-4 border-b border-[#1A1A22]"><p className={title}>Umur Stok (Aging) — {aging.items.length} unit belum terjual</p></div>
      <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#1A1A22] border-b border-[#1A1A22]">
        {aging.buckets.map((b, i) => (
          <div key={b.label} data-testid={`aging-bucket-${i}`} className="p-4">
            <p className="text-xs text-zinc-500">{b.label}</p>
            <p className={`font-heading font-extrabold text-2xl mt-1 ${colors[i]}`}>{b.unit} <span className="text-sm font-medium text-zinc-500">unit</span></p>
            {showHpp && <p className="text-xs text-zinc-400 font-mono mt-1">Modal {fmtCompact(b.modal)}</p>}
          </div>
        ))}
      </div>
      <div className="p-4">
        <p className="text-xs text-zinc-400 mb-2">Unit {">"}60 hari — perlu tindakan (cek harga pasar, promo, atau pindah cabang)</p>
        {slow.length === 0 ? <p className="text-sm text-zinc-500">Tidak ada unit macet. Bagus!</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[620px]">
              <thead><tr className="text-left border-b border-[#1A1A22]"><th className={th}>Mobil</th><th className={th}>Cabang</th><th className={th}>Status</th><th className={`${th} text-right`}>Umur</th>{showHpp && <th className={`${th} text-right`}>Modal</th>}<th className={`${th} text-right`}>Harga Jual</th></tr></thead>
              <tbody>
                {slow.map((i) => (
                  <tr key={i.car_id} data-testid="aging-slow-row" className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50">
                    <td className="px-4 py-2.5"><Link to={`/mobil/${i.car_id}`} className="text-zinc-100 font-medium hover:text-blue-400">{i.car_name}</Link>{i.konsinyasi && <span className="ml-2 text-[10px] text-zinc-500">Konsinyasi</span>}</td>
                    <td className="px-4 py-2.5 text-zinc-400">{i.branch_name}</td>
                    <td className="px-4 py-2.5"><StatusBadge status={i.status} /></td>
                    <td className={`px-4 py-2.5 text-right font-mono font-semibold ${i.days > 90 ? "text-rose-500" : "text-amber-500"}`}>{i.days} hari</td>
                    {showHpp && <td className="px-4 py-2.5 text-right"><Money value={i.modal} className="text-zinc-300" /></td>}
                    <td className="px-4 py-2.5 text-right"><Money value={i.harga_jual} className="text-zinc-100" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function PerfTable({ testid, heading, rows, labelHead, extra = [], note }) {
  return (
    <div data-testid={testid} className={card}>
      <div className="p-4 border-b border-[#1A1A22]"><p className={title}>{heading}</p>{note && <p className="text-xs text-zinc-500 mt-1">{note}</p>}</div>
      {rows.length === 0 ? <p className="p-4 text-sm text-zinc-500">Belum ada penjualan lunas pada periode ini.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead><tr className="text-left border-b border-[#1A1A22]">
              <th className={th}>{labelHead}</th><th className={`${th} text-right`}>Unit</th><th className={`${th} text-right`}>Omzet</th>
              <th className={`${th} text-right`}>Laba</th><th className={`${th} text-right`}>Margin</th><th className={`${th} text-right`}>Laba/Unit</th><th className={`${th} text-right`}>Hari Laku</th>
              {extra.map((e) => <th key={e.key} className={`${th} text-right`}>{e.head}</th>)}
            </tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-b border-[#1A1A22]/60 last:border-0 hover:bg-[#121218]/50">
                  <td className="px-4 py-2.5 font-medium text-zinc-100">{r.label}</td>
                  <td className="px-4 py-2.5 text-right text-zinc-300 font-mono">{r.unit}</td>
                  <td className="px-4 py-2.5 text-right"><Money value={r.omzet} className="text-zinc-300" /></td>
                  <td className="px-4 py-2.5 text-right"><Money value={r.laba} className={`font-semibold ${r.laba >= 0 ? "text-emerald-500" : "text-rose-500"}`} /></td>
                  <td className={`px-4 py-2.5 text-right font-mono ${r.margin >= 0 ? "text-zinc-300" : "text-rose-500"}`}>{num(r.margin, 1)}%</td>
                  <td className="px-4 py-2.5 text-right"><Money value={r.laba_rata} className="text-zinc-300" /></td>
                  <td className="px-4 py-2.5 text-right text-zinc-300 font-mono">{r.hari_rata ? `${num(r.hari_rata)} hr` : "-"}</td>
                  {extra.map((e) => <td key={e.key} className="px-4 py-2.5 text-right">{e.render(r)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CostMix({ heading, rows, testid }) {
  const total = rows.reduce((a, r) => a + r.amount, 0);
  return (
    <div data-testid={testid} className={`${card} p-4`}>
      <p className={`${title} mb-1`}>{heading}</p>
      <p className="text-sm font-mono text-zinc-200 mb-3">Total {fmtIDR(total)}</p>
      {rows.length === 0 ? <p className="text-sm text-zinc-500">Tidak ada biaya pada periode ini.</p> : (
        <div className="h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#27272a" horizontal={false} />
              <XAxis type="number" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} tickFormatter={fmtCompact} />
              <YAxis type="category" dataKey="label" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} width={110} />
              <Tooltip {...tooltipStyle} cursor={{ fill: "#27272a", opacity: 0.4 }} formatter={(v) => fmtIDR(v)} />
              <Bar dataKey="amount" name="Biaya" fill="#f43f5e" radius={[0, 2, 2, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export default function Analisa({ branch, showHpp }) {
  const [[s0, e0]] = useState(() => presetRange("3-bulan"));
  const [start, setStart] = useState(s0);
  const [end, setEnd] = useState(e0);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    const q = new URLSearchParams({ start, end });
    if (branch !== "all") q.set("branch_id", branch);
    setData(null); setErr("");
    api.get(`/reports/analisa?${q}`).then((r) => setData(r.data)).catch(() => setErr("Gagal memuat analisa"));
  }, [start, end, branch]);

  const exportExcel = () => {
    if (!data) return;
    const rows = [["RINGKASAN", "", "", ""], ["Omzet", data.kpi.omzet, "Periode lalu", data.prev.omzet], ["Laba Bersih", data.kpi.laba_bersih, "Periode lalu", data.prev.laba_bersih],
      ["Unit", data.kpi.unit, "Periode lalu", data.prev.unit], ["Margin %", data.kpi.margin.toFixed(1), "Periode lalu", data.prev.margin.toFixed(1)], [],
      ["PER CABANG", "Unit", "Omzet", "Laba Bersih"], ...data.per_cabang.map((r) => [r.label, r.unit, r.omzet, r.laba_bersih]), [],
      ["PER MERK", "Unit", "Omzet", "Laba"], ...data.per_merk.map((r) => [r.label, r.unit, r.omzet, r.laba]), [],
      ["PER SALES", "Unit", "Omzet", "Laba"], ...data.per_sales.map((r) => [r.label, r.unit, r.omzet, r.laba])];
    downloadCsv(`analisa-bisnis-${start}_${end}.csv`, ["Keterangan", "Nilai", "", ""], rows);
  };

  return (
    <div data-testid="analisa-tab">
      <DateRangeBar start={start} end={end} setStart={setStart} setEnd={setEnd} prefix="analisa">
        <ExportButtons prefix="analisa" onExcel={exportExcel} disabled={!data} />
      </DateRangeBar>
      {err && <p data-testid="analisa-error" className="text-sm text-rose-500">{err}</p>}
      {!data && !err ? <div className="h-64 bg-[#0A0A0C] border border-[#1A1A22] rounded-md animate-pulse" /> : data && (
        <div className="space-y-4">
          <p className="text-xs text-zinc-500">Dibandingkan dengan periode sebelumnya: {data.prev_start} s/d {data.prev_end}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Kpi testid="kpi-omzet" label="Omzet" value={fmtCompact(data.kpi.omzet)} cur={data.kpi.omzet} prev={data.prev.omzet} delay={0} />
            <Kpi testid="kpi-laba" label="Laba Bersih" value={fmtCompact(data.kpi.laba_bersih)} cur={data.kpi.laba_bersih} prev={data.prev.laba_bersih} delay={60} />
            <Kpi testid="kpi-margin" label="Margin Bersih" value={`${num(data.kpi.margin, 1)}%`} cur={data.kpi.margin} prev={data.prev.margin} pts delay={120} />
            <Kpi testid="kpi-unit" label="Unit Terjual" value={num(data.kpi.unit)} cur={data.kpi.unit} prev={data.prev.unit} delay={180} />
            <Kpi testid="kpi-laba-unit" label="Laba / Unit" value={fmtCompact(data.kpi.laba_per_unit)} cur={data.kpi.laba_per_unit} prev={data.prev.laba_per_unit} delay={240} />
            <Kpi testid="kpi-hari" label="Rata-rata Hari Laku" value={`${num(data.kpi.hari_stok)} hr`} cur={data.kpi.hari_stok} prev={data.prev.hari_stok} invert delay={300} />
          </div>
          <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
            <div className="xl:col-span-3"><Trend data={data.tren} /></div>
            <div className="xl:col-span-2"><Insights items={data.insights} /></div>
          </div>
          <Aging aging={data.aging} showHpp={showHpp} />
          <PerfTable testid="per-cabang-table" heading="Kinerja per Cabang" labelHead="Cabang" rows={data.per_cabang}
            note="Laba Bersih = laba unit terjual − beban operasional cabang pada periode ini"
            extra={[
              { key: "op", head: "Operasional", render: (r) => <Money value={r.operasional} className="text-rose-400" /> },
              { key: "lb", head: "Laba Bersih", render: (r) => <Money value={r.laba_bersih} className={`font-semibold ${r.laba_bersih >= 0 ? "text-emerald-500" : "text-rose-500"}`} /> },
              { key: "stok", head: "Stok Saat Ini", render: (r) => <span className="font-mono text-zinc-300">{r.stok}</span> },
            ]} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <PerfTable testid="per-merk-table" heading="Merk Terlaris & Paling Menguntungkan" labelHead="Merk" rows={data.per_merk}
              extra={[{ key: "stok", head: "Stok", render: (r) => <span className="font-mono text-zinc-300">{r.stok}</span> }]} />
            <PerfTable testid="per-model-table" heading="Model Paling Menguntungkan" labelHead="Model" rows={data.per_model} />
          </div>
          <PerfTable testid="per-sales-table" heading="Kinerja Sales" labelHead="Sales" rows={data.per_sales}
            extra={[{ key: "komisi", head: "Komisi", render: (r) => <Money value={r.komisi} className="text-zinc-300" /> }]} />
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <CostMix testid="biaya-op-chart" heading="Komposisi Beban Operasional" rows={data.biaya_op} />
            <CostMix testid="biaya-mobil-chart" heading="Komposisi Biaya Mobil (Rekondisi)" rows={data.biaya_mobil} />
          </div>
        </div>
      )}
    </div>
  );
}
